import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const BACKDROP =
  "https://images.unsplash.com/photo-1717013287986-8ade0a66d87c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxteXN0aWNhbCUyMGNlbGVzdGlhbCUyMGdvbGQlMjBzdW4lMjBtb29ufGVufDB8fHx8MTc4MzIxOTIzNXww&ixlib=rb-4.1.0&q=85";

const PALM =
  "https://customer-assets.emergentagent.com/job_f2410923-45b5-41a3-af61-0c43786e0f4d/artifacts/phtrtj13_Screenshot%202026-07-09%20at%209.56.10%E2%80%AFAM.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter your email and password");
      return;
    }
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-end justify-center overflow-hidden bg-soma-surface md:items-center">
      <img src={BACKDROP} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-soma-surface/40 via-soma-surface/70 to-soma-surface" />

      <div className="relative z-10 mx-4 mb-10 w-full max-w-md rounded-3xl border border-soma-border/60 bg-soma-surface2/70 p-8 backdrop-blur-xl md:mb-0">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-6 h-36 w-28 overflow-hidden rounded-2xl border border-soma-brand/40 bg-[#EDE4D1] shadow-xl shadow-black/50">
            <img src={PALM} alt="Antique palmistry chart" className="h-full w-full object-cover object-top" />
          </div>
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.35em] text-soma-brand">// soma linea</p>
          <h1 className="font-display text-5xl italic text-soma-on">Enter the sanctuary</h1>
          <p className="mt-2 text-sm text-soma-on2">Invite-only. For the practitioners holding the container.</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-soma-muted">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              data-testid="login-email-input"
              className="w-full rounded-xl border border-soma-border bg-soma-surface/60 px-4 py-3 text-soma-on outline-none transition-colors focus:border-soma-brand"
              placeholder="you@somalinea.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-soma-muted">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="login-password-input"
              className="w-full rounded-xl border border-soma-border bg-soma-surface/60 px-4 py-3 text-soma-on outline-none transition-colors focus:border-soma-brand"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-soma-brand py-3.5 font-semibold text-soma-surface transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            <Sparkles size={18} />
            {busy ? "Entering…" : "Enter the sanctuary"}
          </button>
        </form>
      </div>
    </div>
  );
}
